import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { HeaderMenuItem } from "../../config/header-menu-items";
import { RootState } from "../../redux/store";

type Props = {
  data: HeaderMenuItem[];
};

const DropdownItem: React.FC<{ item: HeaderMenuItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const [isDesktopHover, setIsDesktopHover] = useState(true);
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateDeviceMode = () => {
      const canHoverLikeDesktop = mediaQuery.matches;
      setIsDesktopHover(canHoverLikeDesktop);
      if (canHoverLikeDesktop) {
        // Prevent stale mobile open state when returning to desktop-sized viewport.
        setOpen(false);
      }
    };

    updateDeviceMode();
    mediaQuery.addEventListener("change", updateDeviceMode);

    return () => {
      mediaQuery.removeEventListener("change", updateDeviceMode);
    };
  }, []);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("touchstart", closeOnOutsideClick);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("touchstart", closeOnOutsideClick);
    };
  }, []);

  const handleMenuClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!isDesktopHover) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <li
      className="nav-item dropdown"
      ref={ref}
      onMouseEnter={() => {
        if (isDesktopHover) setOpen(true);
      }}
      onMouseLeave={() => {
        if (isDesktopHover) setOpen(false);
      }}
    >
      <a
        href="#"
        role="button"
        className="nav-link dropdown-toggle"
        aria-expanded={open}
        onClick={handleMenuClick}
      >
        {item.title} <i className="fa fa-caret-down" />
      </a>
      {open && (
        <div className="dropdown-menu show">
          {item.children?.map((child) => (
            typeof child.link === "string" && child.link === "#" ? (
              <a
                key={child.id}
                href="#"
                className="dropdown-item"
                onClick={(e) => e.preventDefault()}
              >
                <span>{child.title}</span>
              </a>
            ) : (
              <Link
                key={child.id}
                to={typeof child.link === "string" ? child.link : "#"}
                className="dropdown-item"
                onClick={() => setOpen(false)}
              >
                <span>{child.title}</span>
              </Link>
            )
          ))}
        </div>
      )}
    </li>

  );
};

const MenuItems: React.FC<Props> = ({ data }) => {
  const userId = useSelector((state: RootState) => state.user.userData?.user_id) ?? "";

  return (
    <ul className="nav align-items-center main-menu">
      {data.map((d) => {
        if (d.children) {
          return <DropdownItem key={d.id} item={d} />;
        }

        const link = typeof d.link === "string" ? d.link : d.link(userId);

        return (
          <li key={d.id} className="nav-item">
            <Link to={link} className="nav-link">
              {d.title}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

export default MenuItems;
